if (!customElements.get('vf-stock-grid')) {
  class VfStockGrid extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.ready = this.orderProducts();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    cards(grid) {
      return Array.from(grid.children).filter((card) => card.hasAttribute('data-product-card'));
    }

    async orderProducts() {
      const page = Number(this.dataset.vfPage);
      const pages = Number(this.dataset.vfPages);
      const pageSize = Number(this.dataset.vfPageSize);
      const total = Number(this.dataset.vfItems);
      const original = this.cards(this);
      const results = new Map([[page, original]]);
      const baseUrl = new URL(window.location.href);
      const pageParam = this.dataset.vfPageParam || 'page';
      this.setAttribute('aria-busy', 'true');

      try {
        if (![page, pages, pageSize, total].every(Number.isSafeInteger)
          || page < 1 || page > pages || pageSize < 1 || total < 1) {
          throw new Error('Invalid collection pagination');
        }

        const remaining = Array.from({ length: pages }, (_, index) => index + 1)
          .filter((number) => number !== page);

        const worker = async () => {
          while (remaining.length) {
            const number = remaining.shift();
            const url = new URL(baseUrl);
            url.searchParams.set('section_id', this.dataset.id);
            url.searchParams.set(pageParam, String(number));
            const response = await fetch(url, { signal: this.controller.signal });
            if (!response.ok) throw new Error('Collection page unavailable');
            const html = await response.text();
            const grid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
            if (!grid || Number(grid.dataset.vfItems) !== total
              || Number(grid.dataset.vfPage) !== number
              || Number(grid.dataset.vfPages) !== pages) {
              throw new Error('Collection changed while loading');
            }
            results.set(number, this.cards(grid));
          }
        };

        await Promise.all(Array.from({ length: Math.min(3, remaining.length) }, worker));
        if (!this.isConnected || this.controller.signal.aborted) return;

        const allCards = Array.from({ length: pages }, (_, index) => results.get(index + 1)).flat();
        const ids = new Set(allCards.map((card) => card.dataset.productId));
        if (allCards.length !== total || ids.size !== total
          || allCards.some((card) => !['true', 'false'].includes(card.dataset.vfInStock))) {
          throw new Error('Collection product list is incomplete');
        }

        const available = allCards.filter((card) => card.dataset.vfInStock === 'true');
        const unavailable = allCards.filter((card) => card.dataset.vfInStock === 'false');
        const ordered = [...available, ...unavailable];
        const start = (page - 1) * pageSize;
        const visibleCards = ordered.slice(start, start + pageSize);
        if (visibleCards.length !== original.length
          || visibleCards.some((card, index) => card !== original[index])) {
          this.replaceChildren(...visibleCards);
        }
        this.dataset.vfAvailableCount = String(available.length);
        this.dataset.vfStockReady = 'true';
      } catch (error) {
        this.controller.abort();
        if (error.name !== 'AbortError') {
          this.dataset.vfStockReady = 'error';
          console.warn('Collection stock ordering:', error.message);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('vf-stock-grid', VfStockGrid);
}
